/** Reading a node's bindings out of the view's data, so its component gets plain values. */
import type { ComponentDef } from './catalog.ts'
import { isBindable, isBinding, readPath, type Field, type Issue } from './schema.ts'

/** Why `path` read nothing: how far it got, and what was there instead. */
export function explainMissing(scope: Record<string, unknown>, path: string): string {
  const keys = path.split('.')
  let at: unknown = scope
  for (let i = 0; i < keys.length; i++) {
    const here = keys.slice(0, i).join('.') || 'the view'
    if (at === null || at === undefined) return `binds to "${path}", but ${here} is ${String(at)}`
    if (typeof at !== 'object')
      return `binds to "${path}", but ${here} is ${typeof at} ${JSON.stringify(at).slice(0, 40)}, not an object or list`
    const key = keys[i]!
    if (!Object.hasOwn(at, key) && !(Array.isArray(at) && key === 'length')) {
      const have = Array.isArray(at)
        ? `a list of ${at.length}`
        : `fields ${Object.keys(at).slice(0, 12).join(', ') || '(none)'}`
      return `binds to "${path}", but ${here} has no "${key}" (it has ${have})`
    }
    at = (at as Record<string, unknown>)[key]
  }
  return `binds to "${path}", which is empty`
}

function resolveField(
  field: Field,
  value: unknown,
  scope: Record<string, unknown>,
  path: string,
  issues: Issue[]
): unknown {
  if (isBinding(value) && isBindable(field)) {
    const got = readPath(scope, value.$)
    if (got === undefined) issues.push({ path, message: explainMissing(scope, value.$) })
    return got
  }
  if (field.kind === 'array' && Array.isArray(value))
    return value.map((v, i) => resolveField(field.of, v, scope, `${path}[${i}]`, issues))
  if (field.kind === 'record' && value && typeof value === 'object') return resolveDeep(value, scope, path, issues)
  if (field.kind === 'object' && value && typeof value === 'object' && !Array.isArray(value)) {
    const out: Record<string, unknown> = { ...(value as Record<string, unknown>) }
    for (const [k, f] of Object.entries(field.fields)) {
      if (out[k] !== undefined) out[k] = resolveField(f, out[k], scope, `${path}.${k}`, issues)
      if (out[k] === undefined && f.default !== undefined) out[k] = f.default
    }
    return out
  }
  return value
}

/** A record with the bindings anywhere inside it read. */
function resolveDeep(value: unknown, scope: Record<string, unknown>, path: string, issues: Issue[]): unknown {
  if (isBinding(value)) {
    const got = readPath(scope, value.$)
    if (got === undefined) issues.push({ path, message: explainMissing(scope, value.$) })
    return got
  }
  if (Array.isArray(value)) return value.map((v, i) => resolveDeep(v, scope, `${path}[${i}]`, issues))
  if (value && typeof value === 'object')
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, resolveDeep(v, scope, `${path}.${k}`, issues)]))
  return value
}

/**
 * A node's props with bindings read from `scope` and defaults filled in. Child nodes stay as written (they resolve
 * when they render). Bindings that read nothing are issues.
 */
export function resolveProps(
  def: ComponentDef,
  node: Record<string, unknown>,
  scope: Record<string, unknown>
): { props: Record<string, unknown>; issues: Issue[] } {
  const issues: Issue[] = []
  const props: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(def.props)) {
    const v = node[key] === undefined ? undefined : resolveField(field, node[key], scope, key, issues)
    props[key] = v === undefined ? field.default : v
  }
  return { props, issues }
}
